import { column, Css, GridDataRow, GridTable, simpleHeader, useSuperDrawer } from "@homebound/beam";
import { useEffect, useMemo } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useMatch, useNavigate } from "react-router-dom";
import { CostType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import { MaterialSuperDrawer } from "src/routes/libraries/material-catalog/components/material-super-drawer/MaterialSuperDrawer";
import {
  TaskMaterialsPage_PlanTaskFragment,
  TaskMaterialsPage_ProjectItemFragment,
  usePlanTaskMaterialsPageQuery,
} from "src/routes/projects/dynamic-schedules/task-details/TaskMaterialsPage.generated";
import { projectsPath } from "src/routes/routesDef";
import {
  createDraftScheduleUrl,
  createTaskDetailsPageUrl,
  createTaskMaterialsPageUrl,
  createTaskMaterialsPageVariantDetailsUrl,
} from "src/RouteUrls";
import { projectDynamicScheduleTaskMaterialsDetailsRoute } from "src/routing/domains/projectsPaths";
import { queryResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";

export function TaskMaterialsPage() {
  const { planTaskId, projectId } = useRouteParams<{ planTaskId: string; projectId: string }>();
  const query = usePlanTaskMaterialsPageQuery({ variables: { taskId: planTaskId } });
  return queryResult(query, ({ planTask }) => {
    return <TaskMaterialsPageView planTask={planTask} projectId={projectId} />;
  });
}

type TaskMaterialsPageViewProps = { planTask: TaskMaterialsPage_PlanTaskFragment; projectId: string };

function TaskMaterialsPageView({ planTask, projectId }: TaskMaterialsPageViewProps) {
  const { schedule, name, id, projectItems, globalPlanTask } = planTask;
  const superDrawerMatch = useMatch({ path: projectDynamicScheduleTaskMaterialsDetailsRoute.path, end: false });
  const variantId = superDrawerMatch?.params?.variantId;
  const { isDrawerOpen, openInDrawer, closeDrawer } = useSuperDrawer();
  const navigate = useNavigate();

  const materialProjectItems = useMemo(
    () => projectItems.filter((pi) => pi.costType === CostType.Materials),
    [projectItems],
  );

  const pageTitle = useMemo(() => {
    const maybeDropName = globalPlanTask.drops.first?.name;
    return `Materials ${maybeDropName ? `- ${maybeDropName}` : ""}`;
  }, [globalPlanTask]);

  useEffect(() => {
    if (variantId && !isDrawerOpen) {
      openInDrawer({
        content: <MaterialSuperDrawer variantId={variantId} />,
        onClose: () => navigate(createTaskMaterialsPageUrl(projectId, id)),
      });
    }
    if (!variantId && isDrawerOpen) {
      closeDrawer();
    }
  }, [isDrawerOpen, variantId, openInDrawer, closeDrawer, navigate, id, projectId]);
  const rows = useMemo(() => createRows(materialProjectItems), [materialProjectItems]);
  const columns = useMemo(() => createColumns(projectId, id, navigate), [projectId, id, navigate]);
  return (
    <div data-testid="taskMaterialsPage">
      <PageHeader
        title={pageTitle}
        breadcrumb={[
          { label: "Projects", href: projectsPath },
          { label: schedule.parent.name, href: createDraftScheduleUrl(schedule.parent.id) },
          { label: name, href: createTaskDetailsPageUrl(projectId, id) },
        ]}
      />
      <GridTable
        columns={columns}
        rows={rows}
        fallbackMessage={`No Materials on Task ${name}`}
        rowStyles={{
          material: {
            cellCss: Css.sm.$,
            rowLink: (row) =>
              row.data.itemTemplateItem?.materialVariant?.id
                ? createTaskMaterialsPageVariantDetailsUrl(projectId, id, row.data.itemTemplateItem.materialVariant.id)
                : "",
          },
        }}
      />
    </div>
  );
}

type HeaderRow = { kind: "header" };
type MaterialRow = { kind: "material"; data: TaskMaterialsPage_ProjectItemFragment };
type Row = HeaderRow | MaterialRow;

function createRows(projectItems: TaskMaterialsPage_ProjectItemFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...projectItems.map((item) => ({ id: item.id, kind: "material" as const, data: item }))];
}

function createColumns(projectId: string, taskId: string, navigate: NavigateFunction) {
  return [
    column<Row>({
      header: "Image",
      material: ({ itemTemplateItem }) => <MaterialImage itemTemplateItem={itemTemplateItem} />,
      w: "64px",
    }),
    column<Row>({ header: "Material Name", material: (projectItem) => <div>{projectItem.name}</div>, w: 2 }),
    column<Row>({
      header: "Division",
      material: (projectItem) => <div>{projectItem.item.costCode.division.name}</div>,
    }),
    column<Row>({
      header: "Material Code",
      material: ({ itemTemplateItem }) => <div>{itemTemplateItem?.materialVariant?.code}</div>,
      mw: "260px",
    }),
    column<Row>({ header: "Quantity", material: (projectItem) => <div>{projectItem.quantity}</div> }),
    column<Row>({
      header: "SKU",
      material: ({ itemTemplateItem }) => <div>{itemTemplateItem?.materialVariant?.modelNumber}</div>,
    }),
    column<Row>({ header: "Vendor", material: (projectItem) => <div>{projectItem.currentTradePartner?.name}</div> }),
  ];
}

function MaterialImage({ itemTemplateItem }: Pick<TaskMaterialsPage_ProjectItemFragment, "itemTemplateItem">) {
  if (!itemTemplateItem?.materialVariant) return <div />;
  const { materialVariant } = itemTemplateItem;
  return (
    <div css={Css.df.aic.wPx(48).hPx(48).$}>
      <img
        src={materialVariant.featuredImage?.asset?.downloadUrl ?? PRODUCT_FALLBACK_IMG_URL}
        alt={materialVariant.code}
      />
    </div>
  );
}
